import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { FileField, RequestFile, RequestParticipant } from "models/interfaces";
import notifiers from "global/constants/NotificationConstants";
import {
  getRequestTokenFromUrl,
  isTruthy,
  openErrorNotification,
} from "helpers/methods";
import { fieldColorFor } from "screens/SignRequest/SignRequestHelper";
import {
  emitFieldCommit,
  loadSignerAttachments,
} from "../ParticipantScreenService";
import t from "../SignerAttachments.tokens";
import AttachmentUploadDialog from "../components/AttachmentUploadDialog";
import AttachmentPreviewDialog from "../components/AttachmentPreviewDialog";
import { PaperclipIcon } from "../components/SignerExperienceIcons";
import {
  decodeAttachmentValue,
  encodeAttachmentValue,
  SignerAttachmentItem,
  toAttachmentItem,
} from "../components/AttachmentPolicy";

const CustomAttachmentFieldElement = ({
  field,
  dispatch,
  selectedFile,
  disableFields,
  currentReviewer,
}: {
  field: FileField;
  dispatch: Function;
  selectedFile: RequestFile;
  coords?: any;
  disableFields?: boolean;
  currentReviewer?: RequestParticipant;
}) => {
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [previewItem, setPreviewItem] = useState<SignerAttachmentItem | null>(
    null,
  );
  const [items, setItems] = useState<SignerAttachmentItem[]>([]);
  // Files this signer has on the OTHER attachment fields of this envelope. The 5-file limit is shared
  // across all of them, so this field's remaining allowance is maxFiles - (items + usedElsewhere).
  const [usedElsewhere, setUsedElsewhere] = useState<number>(0);

  const requestToken = getRequestTokenFromUrl();
  const isReviewer = isTruthy(currentReviewer?.email);
  const interactive = !!disableFields && !isReviewer && !field.readOnly;

  // `field.value` holds the attachment ids (see encodeAttachmentValue) and is what the signer state
  // machine keys completion off, so it doubles as the count to show before the fetch below returns.
  const attachedIds = decodeAttachmentValue(field.value);
  const count = items.length || attachedIds.length;
  const hasFiles = count > 0;
  const color = fieldColorFor(field.signerIndex);

  const writeValue = useCallback(
    (next: SignerAttachmentItem[]) => {
      setItems(next);
      dispatch({
        type: "UPDATE_FIELD",
        fileId: selectedFile.unsignedPdf,
        fieldId: field.name,
        value: encodeAttachmentValue(next),
      });
      emitFieldCommit();
    },
    [dispatch, field.name, selectedFile.unsignedPdf],
  );

  // Read through refs inside the mount effect below. Putting either in its dependency array would
  // re-run the fetch on every render (`dispatch` is a prop and need not be referentially stable) or on
  // every value change — in both cases turning a one-off sync into a request loop.
  const writeValueRef = useRef(writeValue);
  const fieldValueRef = useRef(field.value);
  useEffect(() => {
    writeValueRef.current = writeValue;
    fieldValueRef.current = field.value;
  });

  // Stops a resolved fetch writing into an unmounted field.
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  const syncFromServer = useCallback(async () => {
    if (!requestToken || isReviewer) return;
    try {
      const saved = await loadSignerAttachments(requestToken);
      if (!mountedRef.current) return;
      const mine = saved.filter(
        (attachment) => attachment.fieldName === field.name,
      );
      setUsedElsewhere(saved.length - mine.length);

      const asItems = mine.map((attachment) => toAttachmentItem(attachment));
      // Re-derive field.value from what is actually stored, because the two can be out of step on a
      // reopen. Uploads reach the server immediately, but field.value only rides along with
      // saveFieldData, which fires every fifth commit — so a signer who attaches one file and closes
      // the tab has the FILE saved and the VALUE lost. areAllFieldsFilled reads field.value, so a
      // required field would then refuse to let them finish while their file sits there on screen.
      // Writing the ids back also clears a stale value when the file was removed elsewhere.
      if ((fieldValueRef.current ?? "") !== encodeAttachmentValue(asItems)) {
        writeValueRef.current(asItems);
      } else {
        setItems(asItems);
      }
    } catch {
      // Non-fatal: the field still opens and the signer can attach. Silent because an envelope with
      // several attachment fields would otherwise raise one toast per field.
    }
  }, [requestToken, isReviewer, field.name]);

  useEffect(() => {
    syncFromServer();
  }, [syncFromServer]);

  const open = () => {
    if (!interactive) return;
    if (!requestToken) {
      openErrorNotification(notifiers.ATTACHMENT_LOAD_FAILED);
      return;
    }
    setDialogOpen(true);
    // Refresh in the background. All the attachment fields on a document mount together, so a field
    // opened after the signer uploaded somewhere else is still holding its mount-time count — without
    // this it would offer 5 slots when only 3 remain, and the server would reject the extras with 409.
    // Cheap: uploads and deletes invalidate the shared fetch, so this is a cache read unless something
    // actually changed.
    syncFromServer();
  };

  const label = hasFiles
    ? `${count} ${count === 1 ? "file" : "files"}`
    : "Attach…";

  const accessibleName = [
    field.toolTip || field.label || "Attachment",
    field.required ? "required" : "",
    hasFiles
      ? `${count} ${count === 1 ? "file" : "files"} attached`
      : "no file attached",
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <>
      <Box
        role="button"
        aria-label={accessibleName}
        aria-haspopup="dialog"
        // No aria-required: it is not a supported attribute on role="button" (it belongs to input
        // roles), so screen readers would ignore it. "required" is carried in the accessible name
        // built above instead.
        aria-disabled={!interactive}
        tabIndex={interactive ? 0 : -1}
        onClick={open}
        onKeyDown={(event) => {
          if (interactive && (event.key === " " || event.key === "Enter")) {
            event.preventDefault();
            open();
          }
        }}
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: t.pill.gap,
          px: t.pill.paddingX,
          boxSizing: "border-box",
          overflow: "hidden",
          color,
          backgroundColor: `${color}1F`,
          border: hasFiles ? `1px solid ${color}` : `1.5px solid ${color}`,
          borderRadius: t.pill.radius,
          cursor: interactive ? "pointer" : "default",
          "&:focus-visible": {
            outline: "none",
            boxShadow: `0 0 0 4px ${color}33`,
          },
        }}
      >
        <PaperclipIcon size={t.pill.iconSize} />
        <Typography
          sx={{
            fontSize: t.pill.fontSize,
            fontWeight: t.pill.fontWeight,
            color,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {label}
        </Typography>
      </Box>

      {isDialogOpen && (
        <AttachmentUploadDialog
          open={isDialogOpen}
          onClose={() => setDialogOpen(false)}
          onConfirm={writeValue}
          onPreview={setPreviewItem}
          initialItems={items}
          usedElsewhere={usedElsewhere}
          requestToken={requestToken}
          fieldName={field.name}
          fieldLabel={field.toolTip || field.label || undefined}
          required={field.required}
        />
      )}

      <AttachmentPreviewDialog
        open={!!previewItem}
        item={previewItem}
        requestToken={requestToken}
        onClose={() => setPreviewItem(null)}
      />
    </>
  );
};

export default CustomAttachmentFieldElement;
